import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Container,
  Avatar,
} from '@mui/material';
import {
  AccountCircle,
  ExitToApp as LogoutIcon,
  Dashboard as DashboardIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
  };

  // Check current page
  const isHospitalDashboard = location.pathname === '/hospital';
  const isMainDashboard = location.pathname === '/';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          backgroundColor: '#1e293b',
          borderBottom: '1px solid #334155'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {/* Logo/Title */}
            <Typography
              variant="h6"
              noWrap
              sx={{
                mr: 4,
                fontWeight: 700,
                color: 'white',
                textDecoration: 'none',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              EDFS Dashboard
            </Typography>

            {/* ✅ NEW: Navigation Buttons */}
            <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
              <Button
                startIcon={<DashboardIcon />}
                onClick={() => navigate('/')}
                sx={{
                  color: isMainDashboard ? '#10b981' : 'white',
                  backgroundColor: isMainDashboard ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                  fontWeight: isMainDashboard ? 700 : 500,
                  px: 2,
                  '&:hover': {
                    backgroundColor: isMainDashboard ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                Screening Dashboard
              </Button>

              <Button
                startIcon={<HospitalIcon />}
                onClick={() => navigate('/hospital')}
                sx={{
                  color: isHospitalDashboard ? '#667eea' : 'white',
                  backgroundColor: isHospitalDashboard ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                  fontWeight: isHospitalDashboard ? 700 : 500,
                  px: 2,
                  '&:hover': {
                    backgroundColor: isHospitalDashboard ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                Referrals Dashboard
              </Button>
            </Box>

            {/* User Info */}
            {user && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {user.facility_name && (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      backgroundColor: '#10b981',
                      '&:hover': { backgroundColor: '#059669' },
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    All Facilities
                  </Button>
                )}
                
                <Typography variant="body2" sx={{ color: 'white', mr: 1 }}>
                  {user.first_name} {user.last_name}
                </Typography>

                <IconButton
                  size="large"
                  onClick={handleMenu}
                  color="inherit"
                >
                  <AccountCircle />
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem onClick={handleLogout}>
                    <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          backgroundColor: '#f8fafc',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;